/*
 * MedexOVA Driver Trip URL — /d/:token
 *
 * Single-file SPA rendering all 6 screens driven by the trip's current status.
 * Uber/Lyft-style flow, MedexOVA palette (forest / bone / slate / rust).
 * No login — the URL token IS the session.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import LiveMap from "../../components/TPGoogleMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tpt/dr`;

// MedexOVA brand
const C = {
  forest: "#2B4C3B",
  forestHover: "#1E3629",
  bone: "#F9F8F6",
  boneEdge: "#E9EFEA",
  slate: "#0F172A",
  slateDim: "#475569",
  slateSoft: "#F1F5F9",
  rust: "#C55A44",
  rustSoft: "#FBE9E4",
  amber: "#B45309",
  amberSoft: "#FEF3C7",
  blue: "#3A7BD5",
  blueSoft: "#E4EEF9",
  violet: "#7C3AED",
  violetSoft: "#EFE9FE",
  ok: "#16A34A",
  line: "#E2E0D8",
};

// Convert FastAPI errors (which can be strings OR pydantic-error arrays) into a
// safe string for React rendering. Prevents "Objects are not valid as a React child".
const formatDetail = (detail, fallback = "Something went wrong") => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (d && typeof d === "object" ? (d.msg || JSON.stringify(d)) : String(d))).join("; ");
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
};


const bandFor = (status) => {
  switch (status) {
    case "ASSIGNED":       return { label: "NEW TRIP OFFER", bg: C.amber, fg: "#fff" };
    case "EN_ROUTE":       return { label: "EN ROUTE TO PICKUP", bg: C.forest, fg: "#fff" };
    case "ARRIVED_PU":     return { label: "ARRIVED AT PICKUP", bg: C.violet, fg: "#fff" };
    case "ON_TRIP":        return { label: "ON TRIP", bg: C.blue, fg: "#fff" };
    case "ARRIVED_DO":     return { label: "ARRIVED AT DROP-OFF", bg: C.violet, fg: "#fff" };
    case "COMPLETED":      return { label: "COMPLETED", bg: C.ok, fg: "#fff" };
    case "NO_SHOW_CONFIRMED": return { label: "NO-SHOW CONFIRMED", bg: C.rust, fg: "#fff" };
    default:               return { label: status || "TRIP", bg: C.slate, fg: "#fff" };
  }
};

// -------- SOS panic button (driver) — same escalation pipeline as the
// rider panic button, tagged origin="driver" server-side (sos.py). ----------
const DriverSOSButton = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sent, setSent] = useState(false);
  const [note, setNote] = useState("");

  const trigger = async () => {
    if (confirming) return;
    setConfirming(true);
    let lat = null, lng = null;
    try {
      if (navigator?.geolocation) {
        const pos = await new Promise((res) => navigator.geolocation.getCurrentPosition(
          (p) => res(p), () => res(null), { timeout: 3000 }
        ));
        if (pos?.coords) { lat = pos.coords.latitude; lng = pos.coords.longitude; }
      }
    } catch { /* geo denied — send without coords */ }
    try {
      await axios.post(`${API}/${token}/sos`, { lat, lng, note: note.trim() || null });
      setSent(true);
      if (navigator?.vibrate) { try { navigator.vibrate([120, 60, 120]); } catch { /* */ } }
    } catch (e) {
      toast.error("Couldn't reach MedexOVA. Please call 911 if this is an emergency.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="driver-sos-btn"
        style={{
          position: "fixed", right: 16, bottom: 16, zIndex: 60,
          width: 56, height: 56, borderRadius: "50%", background: "#B91C1C",
          color: "#fff", border: "none", fontWeight: 800, fontSize: 12,
          boxShadow: "0 6px 20px rgba(185,28,28,0.45)", cursor: "pointer",
        }}
      >SOS</button>
      {open && (
        <div
          data-testid="driver-sos-modal-backdrop"
          onClick={() => !confirming && !sent && setOpen(false)}
          style={{ position: "fixed", inset: 0, zIndex: 70, background: "rgba(15,23,42,0.75)", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <div
            data-testid="driver-sos-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", width: "min(420px, 92vw)", borderRadius: 16, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}
          >
            {sent ? (
              <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 40 }}>✅</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.slate }}>Help is on the way</div>
                <div style={{ fontSize: 13, color: C.slateDim }}>
                  MedexOVA has been alerted and can see your location. If this is a life-threatening emergency, please also call <a href="tel:911" style={{ fontWeight: 700, textDecoration: "underline" }}>911</a>.
                </div>
                <button
                  data-testid="driver-sos-close-btn"
                  onClick={() => { setOpen(false); setSent(false); setNote(""); }}
                  style={{ marginTop: 8, width: "100%", padding: "10px 0", borderRadius: 999, fontWeight: 700, color: "#fff", background: C.forest, border: "none", cursor: "pointer" }}
                >Close</button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.slate }}>Need help right now?</div>
                  <div style={{ fontSize: 13, color: C.slateDim, marginTop: 4 }}>Tap below and MedexOVA dispatch will see your location, trip, and rider. You&apos;ll get a callback within 60 seconds.</div>
                </div>
                <textarea
                  data-testid="driver-sos-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional: describe the situation…"
                  rows={3}
                  style={{ width: "100%", padding: 10, borderRadius: 10, fontSize: 14, border: `1px solid ${C.line}` }}
                />
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => setOpen(false)}
                    disabled={confirming}
                    style={{ flex: 1, padding: "10px 0", borderRadius: 999, fontWeight: 700, background: "#fff", color: C.slateDim, border: `1px solid ${C.line}`, cursor: "pointer" }}
                  >Cancel</button>
                  <button
                    onClick={trigger}
                    disabled={confirming}
                    data-testid="driver-sos-confirm-btn"
                    style={{ flex: 1, padding: "10px 0", borderRadius: 999, fontWeight: 800, color: "#fff", background: "#B91C1C", opacity: confirming ? 0.6 : 1, border: "none", cursor: "pointer" }}
                  >{confirming ? "Sending…" : "Send SOS"}</button>
                </div>
                <div style={{ fontSize: 11, color: C.slateDim, textAlign: "center" }}>
                  For life-threatening emergencies, always call <a href="tel:911" style={{ textDecoration: "underline" }}>911</a> first.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// -------- Shared UI ----------
const Shell = ({ status, children, right, token, showSos = true }) => {
  const band = bandFor(status);
  return (
    <div style={{ minHeight: "100vh", background: C.bone, color: C.slate, display: "flex", flexDirection: "column" }}>
      <div style={{ background: band.bg, color: band.fg, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, letterSpacing: "0.08em", fontSize: 12, textTransform: "uppercase" }}>
        <span data-testid="status-band-label">{band.label}</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{right}</span>
      </div>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: C.slateDim, letterSpacing: "0.12em", textTransform: "uppercase" }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, background: `linear-gradient(135deg,${C.forest},${C.blue})`, display: "grid", placeItems: "center", color: "#fff", fontWeight: 800, fontSize: 12 }}>A</div>
          MedexOVA Medical Transportation
        </div>
        {children}
      </div>
      {showSos && token && <DriverSOSButton token={token} />}
    </div>
  );
};

const Card = ({ title, children, style }) => (
  <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 14, ...style }}>
    {title && <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: C.slateDim, marginBottom: 8 }}>{title}</div>}
    {children}
  </div>
);

const DriverRatingCard = ({ tripId, beaconKey, riderName, priorRating }) => {
  const [stars, setStars] = React.useState(priorRating || 0);
  const [hover, setHover] = React.useState(0);
  const [saved, setSaved] = React.useState(!!priorRating);
  const submit = async (s) => {
    if (saved) return;
    setStars(s);
    if (!beaconKey) { toast.error("Session expired — reopen the ride link."); return; }
    try {
      await axios.post(`${API}/tpt/rides/${tripId}/rate-rider?beacon_key=${encodeURIComponent(beaconKey)}`.replace("/tpt/rides", "/rider/rides"), { stars: s });
      setSaved(true);
      toast.success(`Rider rated ${s} star${s>1?"s":""}.`);
    } catch (e) {
      toast.error(formatDetail(e?.response?.data?.detail, "Rating failed"));
    }
  };
  return (
    <Card title={`Rate ${riderName || "the rider"}`}>
      <div style={{ fontSize: 12, color: C.slateDim, marginBottom: 8 }}>Your rating stays confidential and helps our community.</div>
      <div style={{ display: "flex", justifyContent: "center", gap: 4 }} onMouseLeave={() => setHover(0)}>
        {[1,2,3,4,5].map((s) => (
          <button
            key={s}
            disabled={saved}
            onClick={() => submit(s)}
            onMouseEnter={() => setHover(s)}
            data-testid={`driver-star-${s}`}
            style={{
              background: "transparent", border: "none", cursor: saved ? "default" : "pointer",
              fontSize: 34, lineHeight: 1,
              color: ((hover || stars) >= s) ? "#F59E0B" : "#CBD5E1",
              transition: "transform .12s ease",
              transform: ((hover || stars) >= s && !saved) ? "scale(1.08)" : "scale(1.0)",
            }}
            aria-label={`${s} stars`}
          >★</button>
        ))}
      </div>
      {saved && <div style={{ fontSize: 11, color: C.forest, textAlign: "center", fontWeight: 700, marginTop: 6 }}>Rating submitted · thank you</div>}
    </Card>
  );
};


const Pill = ({ children, tone = "default" }) => {
  const toneMap = {
    default: { bg: C.slateSoft, fg: C.slate, br: C.line },
    warn:    { bg: C.amberSoft, fg: C.amber, br: C.amberSoft },
    ok:      { bg: "#DCFCE7", fg: "#166534", br: "#DCFCE7" },
    rust:    { bg: C.rustSoft, fg: C.rust, br: C.rustSoft },
    blue:    { bg: C.blueSoft, fg: C.blue, br: C.blueSoft },
  };
  const t = toneMap[tone];
  return <span style={{ background: t.bg, color: t.fg, border: `1px solid ${t.br}`, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, marginRight: 6 }}>{children}</span>;
};

const Btn = ({ children, variant = "primary", style, ...rest }) => {
  const map = {
    primary: { bg: C.forest, fg: "#fff" },
    forest:  { bg: C.forest, fg: "#fff" },
    blue:    { bg: C.blue, fg: "#fff" },
    dark:    { bg: C.slate, fg: "#fff" },
    danger:  { bg: C.rust, fg: "#fff" },
    ghost:   { bg: "transparent", fg: C.slateDim, border: `1px solid ${C.line}` },
    outline: { bg: "#fff", fg: C.slate, border: `1px solid ${C.line}` },
  };
  const t = map[variant] || map.primary;
  return (
    <button {...rest} style={{ width: "100%", height: 52, borderRadius: 12, background: t.bg, color: t.fg, fontWeight: 700, fontSize: 15, letterSpacing: "0.02em", border: t.border || "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer", ...style }}>{children}</button>
  );
};

// -------- SCREEN 1: TEASER --------
const TeaserScreen = ({ data, token, onAccept, onDecline }) => {
  const [busy, setBusy] = useState(false);
  const timeoutSec = data?.tp?.offer_timeout_seconds || 300;
  const [left, setLeft] = useState(timeoutSec);
  useEffect(() => {
    const t = setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  const wc = data?.vehicle_requirements?.wheelchair;
  const stretcher = data?.vehicle_requirements?.stretcher;
  const doAccept = async () => { setBusy(true); try { await onAccept(); } finally { setBusy(false); } };
  const doDecline = async () => { setBusy(true); try { await onDecline(); } finally { setBusy(false); } };
  return (
    <Shell status="ASSIGNED" right={`${mm}:${ss}`} token={token}>
      <div style={{ fontSize: 12, color: C.slateDim, marginTop: 4 }}>{data?.tp?.name || "Transportation Provider"}</div>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: "4px 0 0", lineHeight: 1.15 }}>{wc ? "Wheelchair trip" : stretcher ? "Stretcher trip" : "Passenger trip"}</h1>
      <div style={{ fontSize: 13, color: C.slateDim }}>Accept to unlock rider details</div>

      <Card title="Trip preview">
        <div style={{ display: "flex", flexWrap: "wrap", marginTop: 2 }}>
          {data?.estimated_duration_min ? <Pill>⧗ ~{data.estimated_duration_min} min</Pill> : null}
          {data?.estimated_distance_mi ? <Pill>{data.estimated_distance_mi.toFixed?.(1) || data.estimated_distance_mi} mi</Pill> : null}
          {wc ? <Pill tone="warn">♿ Wheelchair</Pill> : null}
          {stretcher ? <Pill tone="warn">Stretcher</Pill> : null}
          {data?.mobility_type && !wc && !stretcher ? <Pill>{data.mobility_type}</Pill> : null}
          <Pill tone="ok">Pickup {new Date(data?.requested_pickup || Date.now()).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</Pill>
        </div>
      </Card>

      <Card title="Route (area only)">
        <div style={{ fontWeight: 600 }}>{data?.pickup_area || "—"}</div>
        <div style={{ color: C.slateDim, fontSize: 12, margin: "4px 0" }}>↓</div>
        <div style={{ fontWeight: 600 }}>{data?.dropoff_area || "—"}</div>
      </Card>

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn variant="primary" onClick={doAccept} disabled={busy} data-testid="btn-accept-trip">✓ ACCEPT TRIP</Btn>
        <Btn variant="outline" onClick={doDecline} disabled={busy} data-testid="btn-decline-trip">Decline</Btn>
        <div style={{ textAlign: "center", fontSize: 11, color: C.slateDim }}>Offer expires in {mm}:{ss}</div>
      </div>
    </Shell>
  );
};

// -------- SCREEN 2 & 3: EN ROUTE / ARRIVED_PU --------
const navHref = (lat, lng, address) => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  // Fall back to the address string when we don't have a geocoded pin yet
  // (common for freshly-booked/manually-entered trips) instead of refusing
  // to navigate at all.
  const dest = (lat && lng) ? `${lat},${lng}` : encodeURIComponent(address || "");
  if (isIOS) return `maps://?daddr=${dest}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
};

const PickupScreen = ({ data, token, onStatus, onNoShow }) => {
  const p = data.pickup || {};
  const r = data.rider || {};
  const isArrived = data.status === "ARRIVED_PU";
  const noShowWaitMin = data?.tp_rules?.no_show_wait_minutes || 5;

  // Wait timer starts when arrived
  const arrivedAt = useMemo(() => {
    const evs = data.timeline || [];
    const ev = [...evs].reverse().find(e => e.event === "ARRIVED_PICKUP");
    return ev ? new Date(ev.ts).getTime() : null;
  }, [data.timeline]);

  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const waitSec = arrivedAt ? Math.floor((now - arrivedAt) / 1000) : 0;
  const waitMM = String(Math.floor(waitSec / 60)).padStart(2, "0");
  const waitSS = String(waitSec % 60).padStart(2, "0");
  const canNoShow = isArrived && waitSec >= noShowWaitMin * 60;

  const call = (phone) => { window.location.href = `tel:${phone}`; };
  const navigateExternal = () => {
    if (p.address || (p.lat && p.lng)) window.location.href = navHref(p.lat, p.lng, p.address);
    else toast.info("No pickup address on file yet");
  };
  const capturePosThen = (ev) => {
    if (!navigator.geolocation) return onStatus(ev, null);
    navigator.geolocation.getCurrentPosition(
      (pos) => onStatus(ev, [pos.coords.latitude, pos.coords.longitude]),
      () => onStatus(ev, null),
      { enableHighAccuracy: true, timeout: 6000 }
    );
  };

  return (
    <Shell status={data.status} right={isArrived ? `WAIT ${waitMM}:${waitSS}` : ""} token={token}>
      <div style={{ fontSize: 12, color: C.slateDim }}>{isArrived ? "Waiting for" : "Pickup"}</div>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: "2px 0" }} data-testid="rider-name">{r.name || "Rider"}</h1>

      <Card title="Pickup address">
        <div style={{ fontWeight: 600 }} data-testid="pickup-address">{p.address || "—"}</div>
        <div style={{ marginTop: 8 }}>
          {r.mobility_type === "wheelchair" && <Pill tone="warn">♿ Wheelchair</Pill>}
          {r.medical_notes ? <Pill tone="rust">medical note</Pill> : null}
          {p.notes ? <Pill>gate/floor</Pill> : null}
        </div>
        {(r.medical_notes || p.notes || r.special_instructions) && (
          <div style={{ marginTop: 10, fontSize: 13, color: C.slate, background: C.slateSoft, padding: 10, borderRadius: 10 }}>
            {r.medical_notes ? <div><b>Medical:</b> {r.medical_notes}</div> : null}
            {p.notes ? <div><b>Location:</b> {p.notes}</div> : null}
            {r.special_instructions ? <div><b>Rider:</b> {r.special_instructions}</div> : null}
          </div>
        )}
      </Card>

      <Card title="Quick contact">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {r.phone && <button onClick={() => call(r.phone)} style={{ background: C.slateSoft, border: "none", padding: "8px 12px", borderRadius: 10, fontWeight: 600, cursor: "pointer" }}>☏ Call {r.name?.split(" ")[0] || "rider"}</button>}
          {p.contact_phone && <button onClick={() => call(p.contact_phone)} style={{ background: C.slateSoft, border: "none", padding: "8px 12px", borderRadius: 10, fontWeight: 600, cursor: "pointer" }}>☏ Call {p.contact_name || "facility"}</button>}
        </div>
      </Card>

      {isArrived && (
        <Card title={`Wait timer · free wait ${data?.tp_rules?.wait_time_minutes || 5} min`}>
          <div style={{ fontSize: 32, fontWeight: 800, fontVariantNumeric: "tabular-nums" }} data-testid="wait-timer">{waitMM}:{waitSS}</div>
          <div style={{ fontSize: 12, color: C.slateDim }}>No-show unlocks at {noShowWaitMin}:00</div>
        </Card>
      )}

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn variant="blue" onClick={navigateExternal} data-testid="btn-navigate-pickup">➤ NAVIGATE TO PICKUP</Btn>
        {!isArrived
          ? <Btn variant="primary" onClick={() => capturePosThen("ARRIVED_PICKUP")} data-testid="btn-arrived-pu">✓ ARRIVED AT PICKUP</Btn>
          : <Btn variant="primary" onClick={() => capturePosThen("PASSENGER_ONBOARD")} data-testid="btn-onboard">✓ PASSENGER ONBOARD</Btn>
        }
        {isArrived && (
          <Btn variant={canNoShow ? "danger" : "ghost"} onClick={canNoShow ? onNoShow : undefined} disabled={!canNoShow} data-testid="btn-no-show">
            {canNoShow ? "⌀ RIDER NO-SHOW" : `Rider No-Show (${Math.max(0, noShowWaitMin * 60 - waitSec)}s to unlock)`}
          </Btn>
        )}
      </div>
    </Shell>
  );
};

// -------- SCREEN 4: ON TRIP (map) --------
const OnTripScreen = ({ data, token, onStatus, driverLive }) => {
  const d = data.dropoff || {};
  const p = data.pickup || {};
  const capturePosThen = (ev) => {
    if (!navigator.geolocation) return onStatus(ev, null);
    navigator.geolocation.getCurrentPosition(
      (pos) => onStatus(ev, [pos.coords.latitude, pos.coords.longitude]),
      () => onStatus(ev, null),
      { enableHighAccuracy: true, timeout: 6000 }
    );
  };
  const navigateExternal = () => {
    if (d.address || (d.lat && d.lng)) window.location.href = navHref(d.lat, d.lng, d.address);
    else toast.info("No drop-off address on file yet");
  };
  const appt = d?.appointment_time ? new Date(d.appointment_time) : null;
  const apptStr = appt ? appt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;
  return (
    <Shell status="ON_TRIP" right={apptStr ? `APPT ${apptStr}` : ""} token={token}>
      <div style={{ height: 260 }}>
        <LiveMap
          pickup={p.lat != null ? { lat: p.lat, lng: p.lng, address: p.address } : null}
          dropoff={d.lat != null ? { lat: d.lat, lng: d.lng, address: d.address, facility_name: d.facility_name } : null}
          drivers={driverLive?.lat ? [{ driver_id: "me", name: "You", last_seen_lat: driverLive.lat, last_seen_lng: driverLive.lng, shift_status: "on_shift" }] : []}
          height={260}
          emptyLabel="Loading route…"
        />
      </div>
      <Card title="Drop-off">
        <div style={{ fontWeight: 700 }} data-testid="dropoff-address">{d.facility_name || d.address || "—"}</div>
        <div style={{ color: C.slateDim, fontSize: 13 }}>{d.address}</div>
        {apptStr && <div style={{ marginTop: 6 }}><Pill tone="ok">Appt {apptStr}</Pill></div>}
      </Card>
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn variant="blue" onClick={navigateExternal} data-testid="btn-navigate-do">➤ NAVIGATE TO DROP-OFF</Btn>
        <Btn variant="primary" onClick={() => capturePosThen("ARRIVED_DROPOFF")} data-testid="btn-arrived-do">✓ ARRIVED AT DROP-OFF</Btn>
      </div>
    </Shell>
  );
};

// ── Consumer completion (card-on-file is the signature, no POD required)
const ConsumerCompleteScreen = ({ data, token, onSubmit }) => {
  const [busy, setBusy] = useState(false);
  const fare = Number(data?.fare_quote?.total || 0);
  const doIt = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Empty body — backend detects source=='consumer' and skips signature/POD checks
      await onSubmit({});
    } finally { setBusy(false); }
  };
  return (
    <Shell status="ARRIVED_DO" right={`$${fare.toFixed(2)}`} token={token}>
      <div style={{ fontSize: 12, color: C.slateDim, textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700 }}>Ready to complete</div>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: "4px 0" }}>Drop-off confirmed?</h1>
      <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <div style={{ fontSize: 10, color: C.slateDim, letterSpacing: "0.12em", fontWeight: 700, textTransform: "uppercase" }}>Rider</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{data?.rider?.name || "Rider"}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: C.slateDim, letterSpacing: "0.12em", fontWeight: 700, textTransform: "uppercase" }}>Dropped at</div>
          <div style={{ fontSize: 14 }}>{data?.dropoff_address || data?.dropoff?.address}</div>
        </div>
        <div style={{ marginTop: 4, padding: 12, background: C.boneEdge, borderRadius: 10 }}>
          <div style={{ fontSize: 10, color: C.slateDim, letterSpacing: "0.12em", fontWeight: 700, textTransform: "uppercase" }}>Fare · charges the rider&apos;s saved card</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: C.forest }}>${fare.toFixed(2)}</div>
          <div style={{ fontSize: 11, color: C.slateDim, marginTop: 2 }}>Your payout tier is applied automatically after capture.</div>
        </div>
      </div>
      <button onClick={doIt} disabled={busy} data-testid="consumer-complete-btn"
        style={{ marginTop: "auto", height: 60, borderRadius: 12, background: C.forest, color: "#fff", border: "none", fontSize: 18, fontWeight: 800, cursor: "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Capturing payment…" : `Complete ride · Capture $${fare.toFixed(2)}`}
      </button>
      <div style={{ textAlign: "center", fontSize: 11, color: C.slateDim }}>
        No signature needed. Card-on-file authorization is captured now.
      </div>
    </Shell>
  );
};


// -------- SCREEN 5: SIGNATURE --------
const SignatureScreen = ({ token, onSubmit, onNoSigOpen }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const [signerName, setSignerName] = useState("");
  const [rel, setRel] = useState("self");
  const [otherText, setOtherText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = C.slate;
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const ptFromEvent = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; lastPt.current = ptFromEvent(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = ptFromEvent(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
  };
  const end = () => { drawing.current = false; lastPt.current = null; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
  };

  const submit = async () => {
    if (!signerName.trim()) { toast.error("Enter the signer's printed name"); return; }
    if (rel === "other" && !otherText.trim()) { toast.error("Enter explanation for 'Other'"); return; }
    // Extract PNG
    const dataUrl = canvasRef.current.toDataURL("image/png");
    // Cheap "is empty" check: sample transparency ratio
    const ctx = canvasRef.current.getContext("2d");
    const img = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
    let nonEmpty = 0;
    for (let i = 3; i < img.data.length; i += 40) if (img.data[i] > 40) { nonEmpty++; if (nonEmpty > 6) break; }
    if (nonEmpty < 6) { toast.error("Please have the rider sign above"); return; }
    setBusy(true);
    try {
      await onSubmit({ signature_b64: dataUrl, signer_name: signerName.trim(), signer_relationship: rel, signer_other_explanation: rel === "other" ? otherText.trim() : null });
    } finally {
      setBusy(false);
    }
  };

  const relOptions = [
    ["self", "Self"],
    ["staff", "Staff"],
    ["driver", "Driver (I'm signing as witness)"],
    ["other", "Other (explain)"],
  ];

  return (
    <Shell status="ARRIVED_DO" right="POD" token={token}>
      <div style={{ fontSize: 12, color: C.slateDim }}>Trip complete — capture signature</div>
      <h1 style={{ fontSize: 24, fontWeight: 800, margin: "2px 0" }}>Rider signature</h1>
      <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, padding: 8 }}>
        <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: 200, background: "#fff", touchAction: "none", borderRadius: 10 }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          data-testid="signature-canvas"
        />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, alignItems: "center" }}>
          <div style={{ fontSize: 11, color: C.slateDim }}>Sign above — X ______________</div>
          <button onClick={clear} style={{ background: "none", border: "none", color: C.blue, fontWeight: 600, cursor: "pointer" }}>Clear</button>
        </div>
      </div>

      <Card title="Printed name">
        <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Full name of signer" data-testid="signer-name-input"
          style={{ width: "100%", height: 44, border: `1px solid ${C.line}`, borderRadius: 10, padding: "0 12px", fontSize: 15 }}/>
      </Card>

      <Card title="Signer is">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {relOptions.map(([val, label]) => (
            <label key={val} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: "pointer" }} data-testid={`signer-rel-${val}`}>
              <input type="radio" name="rel" checked={rel === val} onChange={() => setRel(val)} style={{ accentColor: C.forest }}/>
              {label}
            </label>
          ))}
          {rel === "other" && (
            <input value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="Explanation (required)" data-testid="signer-other-input"
              style={{ width: "100%", height: 40, border: `1px solid ${C.line}`, borderRadius: 10, padding: "0 12px", fontSize: 14, marginTop: 4 }}/>
          )}
        </div>
      </Card>

      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn variant="primary" onClick={submit} disabled={busy} data-testid="btn-submit-sig">✓ SUBMIT & FINISH</Btn>
        <button onClick={onNoSigOpen} style={{ background: "none", border: "none", color: C.slateDim, fontSize: 13, textDecoration: "underline", cursor: "pointer" }} data-testid="btn-no-signature">
          Cannot get signature — record reason instead
        </button>
      </div>
    </Shell>
  );
};

// -------- SCREEN 5b: NO-SIGNATURE FLOW --------
const NoSigScreen = ({ token, onSubmit, onBack }) => {
  const [reason, setReason] = useState("cognitive_impairment");
  const [other, setOther] = useState("");
  const [photoB64, setPhotoB64] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pickPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setPhotoB64(rd.result);
    rd.readAsDataURL(f);
  };
  const submit = async () => {
    if (!photoB64) { toast.error("Drop-off photo is required"); return; }
    if (reason === "other" && !other.trim()) { toast.error("Enter explanation"); return; }
    setBusy(true);
    try {
      await onSubmit({ no_signature_reason: reason, no_signature_other: reason === "other" ? other.trim() : null, backup_photo_b64: photoB64 });
    } finally { setBusy(false); }
  };
  const opts = [
    ["cognitive_impairment", "Cognitive impairment"],
    ["minor", "Minor / unaccompanied"],
    ["refused", "Rider refused"],
    ["facility_staff_signed", "Facility staff signed on paper"],
    ["other", "Other (explain)"],
  ];
  return (
    <Shell status="ARRIVED_DO" right="NO-SIG POD" token={token}>
      <div style={{ fontSize: 12, color: C.slateDim }}>Alternate POD</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: "2px 0" }}>Why can&apos;t the rider sign?</h1>
      <Card title="Reason">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {opts.map(([v, l]) => (
            <label key={v} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: "pointer" }} data-testid={`nosig-reason-${v}`}>
              <input type="radio" name="reason" checked={reason === v} onChange={() => setReason(v)} style={{ accentColor: C.forest }}/>
              {l}
            </label>
          ))}
          {reason === "other" && (
            <input value={other} onChange={(e) => setOther(e.target.value)} placeholder="Explanation" style={{ height: 40, border: `1px solid ${C.line}`, borderRadius: 10, padding: "0 12px", fontSize: 14 }} data-testid="nosig-other-input"/>
          )}
        </div>
      </Card>
      <Card title={<>Drop-off photo <Pill tone="warn">required</Pill></>}>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={pickPhoto} style={{ display: "none" }} data-testid="nosig-photo-input"/>
        <Btn variant="outline" onClick={() => fileRef.current?.click()}>📷 {photoB64 ? "Retake" : "Take"} drop-off photo</Btn>
        {photoB64 && <img src={photoB64} alt="drop-off" style={{ width: "100%", marginTop: 10, borderRadius: 10, maxHeight: 160, objectFit: "cover" }}/>}
      </Card>
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn variant="primary" onClick={submit} disabled={busy} data-testid="btn-submit-nosig">SUBMIT NO-SIG POD</Btn>
        <Btn variant="ghost" onClick={onBack}>Back to signature</Btn>
      </div>
    </Shell>
  );
};

// -------- SCREEN 5c: NO-SHOW FLOW --------
const NoShowScreen = ({ data, token, onSubmit, onBack }) => {
  const noShowWaitMin = data?.tp_rules?.no_show_wait_minutes || 5;
  const arrivedAt = useMemo(() => {
    const ev = [...(data.timeline || [])].reverse().find(e => e.event === "ARRIVED_PICKUP");
    return ev ? new Date(ev.ts).getTime() : Date.now();
  }, [data.timeline]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const waitSec = Math.floor((now - arrivedAt) / 1000);
  const mm = String(Math.floor(waitSec / 60)).padStart(2, "0");
  const ss = String(waitSec % 60).padStart(2, "0");
  const canSubmit = waitSec >= noShowWaitMin * 60;
  const [notes, setNotes] = useState("");
  const [photoB64, setPhotoB64] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pickPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setPhotoB64(rd.result);
    rd.readAsDataURL(f);
  };
  const submit = () => {
    if (!photoB64) { toast.error("Pickup-location photo required"); return; }
    setBusy(true);
    const capture = (gps) => onSubmit({ wait_seconds: waitSec, notes, photo_b64: photoB64, gps }).finally(() => setBusy(false));
    if (navigator.geolocation) navigator.geolocation.getCurrentPosition(
      (pos) => capture([pos.coords.latitude, pos.coords.longitude]),
      () => capture(null),
      { enableHighAccuracy: true, timeout: 6000 }
    );
    else capture(null);
  };
  return (
    <Shell status="ARRIVED_PU" right={`WAIT ${mm}:${ss}`}>
      <div style={{ fontSize: 12, color: C.slateDim }}>Confirm rider no-show</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, margin: "2px 0" }}>{data?.rider?.name || "Rider"} — no-show?</h1>
      <Card title={`Wait timer · required ${noShowWaitMin} min`}>
        <div style={{ fontSize: 32, fontWeight: 800 }} data-testid="ns-wait">{mm}:{ss}</div>
        <div><Pill tone={canSubmit ? "ok" : "warn"}>{canSubmit ? "≥ 5 min ✓" : `waiting…`}</Pill></div>
      </Card>
      <Card title={<>Pickup-location photo <Pill tone="warn">required</Pill></>}>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={pickPhoto} style={{ display: "none" }} data-testid="ns-photo-input"/>
        <Btn variant="outline" onClick={() => fileRef.current?.click()}>📷 {photoB64 ? "Retake" : "Take"} pickup photo</Btn>
        {photoB64 && <img src={photoB64} alt="pickup" style={{ width: "100%", marginTop: 10, borderRadius: 10, maxHeight: 160, objectFit: "cover" }}/>}
      </Card>
      <Card title="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="e.g. Knocked twice, no answer at door 4B." style={{ width: "100%", border: `1px solid ${C.line}`, borderRadius: 10, padding: 10, fontSize: 14 }} data-testid="ns-notes"/>
      </Card>
      <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        <Btn variant="danger" onClick={canSubmit ? submit : undefined} disabled={!canSubmit || busy} data-testid="btn-confirm-no-show">CONFIRM NO-SHOW</Btn>
        <Btn variant="ghost" onClick={onBack}>Cancel</Btn>
      </div>
    </Shell>
  );
};

// -------- SCREEN 6: RECEIPT --------
const ReceiptScreen = ({ data, onAdvanceChain }) => {
  const evByName = (name) => (data.timeline || []).find(e => e.event === name);
  const t = {
    accepted: evByName("TRIP_ACCEPTED"),
    arrived_pu: evByName("ARRIVED_PICKUP"),
    onboard: evByName("PASSENGER_ONBOARD"),
    arrived_do: evByName("ARRIVED_DROPOFF"),
    completed: evByName("TRIP_COMPLETED") || evByName("NO_SHOW_CONFIRMED"),
  };
  const fmt = (ts) => ts ? new Date(ts.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
  const isNoShow = data.status === "NO_SHOW_CONFIRMED";
  const nextLeg = data.chain?.next_leg;
  const [advancing, setAdvancing] = useState(false);
  return (
    <Shell status={data.status} right={data.chain ? `STOP ${data.chain.sequence} OF ${data.chain.total_legs}` : "24H RECEIPT"}>
      {nextLeg && (
        <div data-testid="chain-next-stop-card" style={{ background: C.forest, color: "#fff", borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", opacity: 0.85 }}>NOVA'S ROUTE · STOP {data.chain.sequence + 1} OF {data.chain.total_legs} IS QUEUED</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{nextLeg.rider_first || "Next rider"} · {nextLeg.pickup_address}</div>
          {nextLeg.planned_pickup && <div style={{ fontSize: 12, opacity: 0.85 }}>Planned pickup ~{new Date(nextLeg.planned_pickup).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
          <button data-testid="btn-advance-chain" disabled={advancing}
            onClick={async () => { setAdvancing(true); await onAdvanceChain(); }}
            style={{ background: "#fff", color: C.forest, border: "none", borderRadius: 10, padding: "12px 16px", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
            {advancing ? "Loading next stop…" : "Ready for next stop →"}
          </button>
        </div>
      )}
      <div style={{ display: "grid", placeItems: "center", padding: "4px 0" }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: isNoShow ? C.rust : C.ok, color: "#fff", display: "grid", placeItems: "center", fontSize: 26, fontWeight: 900 }}>✓</div>
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, textAlign: "center" }}>{isNoShow ? "No-show recorded" : nextLeg ? "Stop complete" : "Trip complete"}</h1>
      <div style={{ textAlign: "center", color: C.slateDim, fontSize: 13 }}>{data.rider_first} · {data.pickup_area} → {data.dropoff_area}</div>
      <Card title="Timeline">
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span>Accepted</span><b>{fmt(t.accepted)}</b></div>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span>Pickup arr.</span><b>{fmt(t.arrived_pu)}</b></div>
          {!isNoShow && <div style={{ display: "flex", justifyContent: "space-between" }}><span>Onboard</span><b>{fmt(t.onboard)}</b></div>}
          {!isNoShow && <div style={{ display: "flex", justifyContent: "space-between" }}><span>Drop-off</span><b>{fmt(t.arrived_do)}</b></div>}
          <div style={{ display: "flex", justifyContent: "space-between" }}><span>{isNoShow ? "No-show" : "Signed"}</span><b>{fmt(t.completed)}</b></div>
        </div>
      </Card>
      {data.pod && data.pod.signer_name && (
        <Card title="Proof of delivery">
          <div style={{ fontSize: 13 }}>Signed by <b>{data.pod.signer_name}</b> ({data.pod.signer_relationship})</div>
          {data.pod.hash && <div style={{ fontSize: 11, color: C.slateDim, marginTop: 4, wordBreak: "break-all" }}>{data.pod.hash.slice(0, 24)}…</div>}
        </Card>
      )}
      {data.source === "consumer" && data.driver_payout_amount != null && (
        <Card title={`Your payout · ${data.driver_tier_badge || ""} ${data.driver_tier_label || ""}`}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700 }}>
            <span>You earned</span>
            <span style={{ color: C.forest }}>${Number(data.driver_payout_amount).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.slateDim, marginTop: 4 }}>
            <span>Rider paid</span>
            <span>${Number(data.fare_quote?.total || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.slateDim, marginTop: 2 }}>
            <span>MedexOVA fee ({Math.round((1 - (data.driver_pct || 0)) * 100)}%)</span>
            <span>${Number(data.platform_fee_amount || 0).toFixed(2)}</span>
          </div>
          <div style={{ marginTop: 8, padding: 8, background: C.boneEdge, borderRadius: 8, fontSize: 11, color: C.slateDim }}>
            Industry rideshare platforms take 30–40%. You just kept {Math.round((data.driver_pct || 0) * 100)}%. Keep driving to reach your next partner tier.
          </div>
        </Card>
      )}
      {data.source === "consumer" && data.payment_status === "captured" && data.stripe_payment_intent_id && (
        <Card title="Payment">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
            <span style={{ color: C.slateDim }}>Status</span>
            <span style={{ background: C.ok, color: "#fff", padding: "2px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>✓ Captured</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 6 }}>
            <span style={{ color: C.slateDim }}>Amount captured</span>
            <span style={{ fontWeight: 700 }}>${Number(data.fare_quote?.total || 0).toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.slateDim, marginTop: 6, gap: 8 }}>
            <span>Stripe ref</span>
            <span style={{ fontFamily: "monospace", fontSize: 10, textAlign: "right", wordBreak: "break-all" }}>{data.stripe_payment_intent_id}</span>
          </div>
        </Card>
      )}
      {data.source === "consumer" && (
        <DriverRatingCard tripId={data.trip_id} beaconKey={data.driver_beacon_key} riderName={data.rider?.name || "the rider"} priorRating={data.rider_rating?.stars} />
      )}
      {data.no_show && (
        <Card title="No-show detail">
          <div style={{ fontSize: 13 }}>Waited {Math.floor((data.no_show.wait_seconds||0)/60)} min · confirmed with photo evidence</div>
        </Card>
      )}
      <div style={{ marginTop: "auto", textAlign: "center", color: C.slateDim, fontSize: 12 }}>This link expires 24h after completion.</div>
    </Shell>
  );
};

// -------- ROOT --------
const LoadingScreen = ({ note }) => (
  <div style={{ minHeight: "100vh", background: C.bone, display: "grid", placeItems: "center" }}>
    <div style={{ textAlign: "center", color: C.slateDim, fontSize: 14 }}>{note || "Loading…"}</div>
  </div>
);

const ErrorScreen = ({ message, code }) => (
  <div style={{ minHeight: "100vh", background: C.bone, display: "grid", placeItems: "center", padding: 24 }}>
    <div style={{ textAlign: "center", maxWidth: 400 }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>⌀</div>
      <h1 style={{ fontSize: 20, fontWeight: 800, marginBottom: 6 }}>Link unavailable</h1>
      <div style={{ color: C.slateDim, fontSize: 14 }}>{message || "This link is no longer valid."}</div>
      {code && <div style={{ marginTop: 8, fontSize: 11, color: C.slateDim }}>({code})</div>}
    </div>
  </div>
);

export default function DriverTripApp() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [subScreen, setSubScreen] = useState(null); // "nosig" | "noshow" | null
  const [driverLive, setDriverLive] = useState(null); // { lat, lng }
  const gpsBufferRef = useRef([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/${token}`);
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr({ message: formatDetail(e?.response?.data?.detail, "Cannot open trip"), code: e?.response?.status });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Poll every 8s while trip is active (cheap; SSE would be nicer)
  useEffect(() => {
    if (!data || data.view === "receipt") return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [data, load]);

  // GPS ping loop — every 10s once accepted, flushes buffered pings
  useEffect(() => {
    if (!data || data.view !== "full") return;
    if (!navigator.geolocation) return;
    const active = ["EN_ROUTE", "ARRIVED_PU", "ON_TRIP", "ARRIVED_DO"].includes(data.status);
    if (!active) return;
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setDriverLive({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          gpsBufferRef.current.push({
            ts: new Date().toISOString(),
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            speed: pos.coords.speed,
            heading: pos.coords.heading,
          });
          if (gpsBufferRef.current.length >= 3) {
            const batch = gpsBufferRef.current.splice(0, gpsBufferRef.current.length);
            axios.post(`${API}/${token}/gps-ping`, { pings: batch }).catch(() => gpsBufferRef.current.unshift(...batch));
          }
        },
        () => {},
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 5000 }
      );
    };
    tick();
    const iv = setInterval(tick, 10000);
    return () => { stopped = true; clearInterval(iv); };
  }, [data, token]);

  const doAccept = async () => {
    try {
      const r = await axios.post(`${API}/${token}/accept`);
      setData(r.data);
      toast.success("Trip accepted");
    } catch (e) { toast.error(formatDetail(e?.response?.data?.detail, "Accept failed")); }
  };

  const doDecline = async () => {
    if (!window.confirm("Decline this trip? Your TP will need to reassign.")) return;
    try {
      await axios.post(`${API}/${token}/decline`);
      toast.info("Trip declined");
      await load();
    } catch (e) { toast.error(formatDetail(e?.response?.data?.detail, "Decline failed")); }
  };

  const doStatus = async (event, gps) => {
    try {
      const r = await axios.post(`${API}/${token}/status`, { event, gps });
      setData(r.data);
    } catch (e) { toast.error(formatDetail(e?.response?.data?.detail, "Status update failed")); }
  };

  const doComplete = async (body) => {
    try {
      await axios.post(`${API}/${token}/complete`, body);
      toast.success("POD submitted");
      setSubScreen(null);
      await load();
    } catch (e) { toast.error(formatDetail(e?.response?.data?.detail, "Complete failed")); }
  };

  const doNoShow = async (body) => {
    try {
      await axios.post(`${API}/${token}/no-show`, body);
      toast.success("No-show recorded");
      setSubScreen(null);
      await load();
    } catch (e) { toast.error(formatDetail(e?.response?.data?.detail, "No-show failed")); }
  };

  const doAdvanceChain = async () => {
    try {
      const r = await axios.post(`${API}/${token}/advance-chain`);
      toast.success(`Next stop · ${r.data.sequence} of ${r.data.total_legs}`);
      window.location.href = `/d/${r.data.next_token}`;
    } catch (e) { toast.error(formatDetail(e?.response?.data?.detail, "Couldn't advance to next stop")); }
  };

  if (err) return <ErrorScreen message={err.message} code={err.code}/>;
  if (!data) return <LoadingScreen/>;

  const status = data.status;
  const view = data.view;

  return (
    <>
      <Toaster position="top-center" richColors />
      {view === "teaser" && (
        <TeaserScreen data={data} token={token} onAccept={doAccept} onDecline={doDecline}/>
      )}
      {view === "full" && ["EN_ROUTE", "ARRIVED_PU"].includes(status) && !subScreen && (
        <PickupScreen data={data} token={token} onStatus={doStatus} onNoShow={() => setSubScreen("noshow")}/>
      )}
      {view === "full" && status === "ON_TRIP" && !subScreen && (
        <OnTripScreen data={data} token={token} onStatus={doStatus} driverLive={driverLive}/>
      )}
      {view === "full" && status === "ARRIVED_DO" && !subScreen && (
        data?.source === "consumer"
          ? <ConsumerCompleteScreen data={data} token={token} onSubmit={doComplete}/>
          : <SignatureScreen token={token} onSubmit={doComplete} onNoSigOpen={() => setSubScreen("nosig")}/>
      )}
      {subScreen === "nosig" && (
        <NoSigScreen token={token} onSubmit={doComplete} onBack={() => setSubScreen(null)}/>
      )}
      {subScreen === "noshow" && (
        <NoShowScreen data={data} token={token} onSubmit={doNoShow} onBack={() => setSubScreen(null)}/>
      )}
      {view === "receipt" && (
        <ReceiptScreen data={data} onAdvanceChain={doAdvanceChain}/>
      )}
    </>
  );
}
